import React, { useState } from 'react';
import { auth } from '../utils/api';
import { setSession } from '../utils/auth';
import { useT } from '../i18n/I18nContext';

export default function Login({ onLogin }) {
  const t = useT();
  const [email, setEmail]       = useState('');
  const [password, setPassword] = useState('');
  const [error, setError]       = useState('');
  const [loading, setLoading]   = useState(false);
  // Quando o token vence, o interceptor da API marca o motivo antes de trazer
  // para cá. Sem isso o usuário só via telas zeradas, sem entender por quê.
  const [expired] = useState(() => {
    try {
      const flag = sessionStorage.getItem('charth_session_expired') === '1';
      if (flag) sessionStorage.removeItem('charth_session_expired');
      return flag;
    } catch { return false; }
  });

  const [showRecovery, setShowRecovery] = useState(false);
  const [recoveryEmail, setRecoveryEmail] = useState('');
  const [recoveryMsg, setRecoveryMsg]   = useState('');
  const [recoveryErr, setRecoveryErr]   = useState('');
  const [recoveryLoading, setRecoveryLoading] = useState(false);

  async function handleSubmit(e) {
    e.preventDefault();
    setError(''); setLoading(true);
    try {
      let result;
      try {
        result = await auth.login(email, password);
      } catch (userErr) {
        if (userErr.response?.status === 401) {
          result = await auth.agentLogin(email, password);
        } else {
          throw userErr;
        }
      }
      const { token, user } = result;
      setSession(token, user);
      onLogin(user);
    } catch (err) {
      setError(err.response?.data?.error || t('login.error.default'));
    } finally { setLoading(false); }
  }

  async function handleRecovery(e) {
    e.preventDefault();
    setRecoveryErr(''); setRecoveryMsg(''); setRecoveryLoading(true);
    try {
      await auth.forgotPassword(recoveryEmail);
      setRecoveryMsg(t('login.recover.success'));
      setRecoveryEmail('');
    } catch (err) {
      setRecoveryErr(err.response?.data?.error || t('login.error.recovery'));
    } finally { setRecoveryLoading(false); }
  }

  return (
    <div style={{ minHeight:'100vh', display:'flex', alignItems:'center', justifyContent:'center', background:'var(--pantone)', position:'relative', overflow:'hidden' }}>
      <div style={{ position:'absolute', inset:0, opacity:.06, backgroundImage:'repeating-linear-gradient(45deg, white 0, white 1px, transparent 0, transparent 50%)', backgroundSize:'20px 20px' }} />

      <div style={{ background:'white', borderRadius:20, padding:'48px 44px', width:'100%', maxWidth:420, boxShadow:'0 24px 64px rgba(0,0,0,.25)', position:'relative', zIndex:1 }}>
        <div style={{ textAlign:'center', marginBottom:32 }}>
          <img src="/logo-charth.png" alt="Charth" style={{ width:130, filter:'none' }} />
          <div style={{ marginTop:10, fontSize:13, color:'var(--gray-500)', letterSpacing:.5 }}>{t('login.system_label')}</div>
        </div>

        {!showRecovery ? (
          <>
            {expired && !error && (
              <div style={{ background:'#FFFBEB', border:'1px solid #FDE68A', borderRadius:'var(--radius)', padding:'10px 14px', marginBottom:20, fontSize:13, color:'#B45309' }}>
                ⏳ {t('login.session_expired')}
              </div>
            )}

            {error && (
              <div style={{ background:'var(--red-light)', border:'1px solid #f5c6c2', borderRadius:'var(--radius)', padding:'10px 14px', marginBottom:20, fontSize:13, color:'var(--red)' }}>
                ❌ {error}
              </div>
            )}

            <form onSubmit={handleSubmit}>
              <div className="form-group" style={{ marginBottom:16 }}>
                <label>{t('login.email')}</label>
                <input type="email" value={email} onChange={e=>setEmail(e.target.value)} placeholder="seu@email.com" required autoFocus />
              </div>
              <div className="form-group" style={{ marginBottom:24 }}>
                <label>{t('login.password')}</label>
                <input type="password" value={password} onChange={e=>setPassword(e.target.value)} placeholder="••••••••" required />
              </div>
              <button type="submit" className="btn btn-primary w-full" style={{ justifyContent:'center', padding:'12px', fontSize:15 }} disabled={loading}>
                {loading ? t('login.submitting') : t('login.submit')}
              </button>
            </form>

            <div style={{ marginTop:20, textAlign:'center' }}>
              <button
                type="button"
                onClick={() => { setShowRecovery(true); setError(''); }}
                style={{ background:'none', border:'none', cursor:'pointer', color:'var(--pantone)', fontSize:13, textDecoration:'underline', padding:0 }}
              >
                {t('login.forgot')}
              </button>
            </div>
          </>
        ) : (
          <>
            <div style={{ marginBottom:20 }}>
              <div style={{ fontWeight:600, fontSize:16, color:'var(--gray-800)', marginBottom:6 }}>{t('login.recover.title')}</div>
              <div style={{ fontSize:13, color:'var(--gray-500)' }}>{t('login.recover.desc')}</div>
            </div>

            {recoveryErr && (
              <div style={{ background:'var(--red-light)', border:'1px solid #f5c6c2', borderRadius:'var(--radius)', padding:'10px 14px', marginBottom:16, fontSize:13, color:'var(--red)' }}>
                ❌ {recoveryErr}
              </div>
            )}
            {recoveryMsg && (
              <div style={{ background:'#e8f5e9', border:'1px solid #a5d6a7', borderRadius:'var(--radius)', padding:'10px 14px', marginBottom:16, fontSize:13, color:'#2e7d32' }}>
                ✅ {recoveryMsg}
              </div>
            )}

            {!recoveryMsg && (
              <form onSubmit={handleRecovery}>
                <div className="form-group" style={{ marginBottom:20 }}>
                  <label>{t('login.email')}</label>
                  <input type="email" value={recoveryEmail} onChange={e=>setRecoveryEmail(e.target.value)} placeholder="seu@email.com" required autoFocus />
                </div>
                <button type="submit" className="btn btn-primary w-full" style={{ justifyContent:'center', padding:'12px', fontSize:15 }} disabled={recoveryLoading}>
                  {recoveryLoading ? t('login.recover.sending') : t('login.recover.send')}
                </button>
              </form>
            )}

            <div style={{ marginTop:20, textAlign:'center' }}>
              <button
                type="button"
                onClick={() => { setShowRecovery(false); setRecoveryMsg(''); setRecoveryErr(''); setRecoveryEmail(''); }}
                style={{ background:'none', border:'none', cursor:'pointer', color:'var(--pantone)', fontSize:13, textDecoration:'underline', padding:0 }}
              >
                {t('login.recover.back')}
              </button>
            </div>
          </>
        )}

        <div style={{ marginTop:24, textAlign:'center', fontSize:12, color:'var(--gray-400)' }}>
          Charth Sourcing System © 2026
        </div>
      </div>
    </div>
  );
}
